import LegalLayout from "@/components/legal/LegalLayout";
import { LegalSection, LegalList } from "@/components/legal/LegalSection";

export default function TermsOfService() {
  return (
    <LegalLayout
      title="Terms of Service"
      lastUpdated="October 21, 2026"
      current="terms-of-service"
    >
      <p className="text-[16px] text-black/80 mb-10 leading-[1.8]">
        These Terms of Service ("Terms") govern your access to and use of the
        Zynthoro platform operated by{" "}
        <strong>Casa Haya International BV</strong> (KvK 99196581), a private
        limited company registered in the Netherlands. By creating an account,
        you accept these Terms in full.
      </p>

      <LegalSection id="service" number="1" title="The service">
        <p>
          Zynthoro provides business software and AI-supported workflows.
          Starter, Creator, Business and Agency are non-ERP subscriptions.
          Enterprise provides ERP functionality. Modules, workspaces, users
          and integrations depend on the purchased plan; the overall platform
          description does not mean that every plan includes every feature.
        </p>
      </LegalSection>

      <LegalSection id="eligibility" number="2" title="Eligibility & account">
        <LegalList
          items={[
            "You must be 18 years or older and authorised to bind your company.",
            "You must provide accurate account details. The fictional demo uses prefilled Elvayae company data; your own business details belong in your purchased workspace.",
            "You are responsible for all activity under your account, including invited team members.",
            "Two-factor authentication is strongly recommended; on Enterprise plans it is mandatory.",
          ]}
        />
      </LegalSection>

      <LegalSection id="plans" number="3" title="Plans, billing & Kickstart lifetime deals">
        <LegalList
          items={[
            "Standard subscription pricing: Starter €499/mo, Creator €699/mo, Business €899/mo, Agency €1,199/mo, Enterprise from €2,499/mo. For new Starter, Creator, Business and Agency contracts, the proposed minimum initial commitment is 12 months, even with monthly billing, subject to explicit acceptance in the order and applicable law. Annual billing (2 months free) is available on all subscription plans.",
            "Kickstart is a one-time lifetime licence for one solo business user without personnel: Kickstart 1 €79 includes 50 total AI credits, Kickstart 2 €149 includes 150 and Kickstart 3 €199 includes 300. Included credits do not reset monthly and have no scheduled expiry. Non-AI functions remain available within the purchased tier when AI credits run out.",
            "Additional Kickstart packages: AI+Social Week provides 30 credits for €24.99 with seven days of validity; AI+Social Month provides 150 credits for €59.99 with thirty days of validity. Additional credits do not unlock subscription-only functions or change the base licence. Their validity is separate from the included lifetime-credit balance.",
            "Subscriptions are billed in advance via Stripe in EUR. All fees are exclusive of VAT, which is added where legally required.",
            "Failed payments on subscriptions trigger a 7-day grace period before service suspension.",
          ]}
        />
      </LegalSection>

      <LegalSection id="lifetime-terms" number="3a" title="Kickstart lifetime — specific terms">
        <p className="mb-3">
          "Lifetime" means a perpetual right to use the purchased Kickstart
          service for an unlimited duration, without an expiry date or recurring
          licence-renewal charge. It is not limited to the period during which
          Zynthoro chooses to sell Kickstart. The purchased tier's entitlements
          remain applicable; lifetime does not mean unlimited AI use or access
          to subscription-only functions.
        </p>
        <p className="mb-3">
          A voluntary upgrade to Starter, Creator, Business, Agency or Enterprise
          permanently relinquishes the Kickstart licence and all remaining
          Kickstart AI credits, including unused included and additional credits.
          They are not carried into the subscription or restored when it ends.
          These consequences must be clearly disclosed before upgrade confirmation.
        </p>
        <p>
          Audio recording, transcription of recordings and generation of reports
          from recordings are excluded from Kickstart. Purchasing additional
          credits does not unlock these subscription-only functions.
        </p>
      </LegalSection>

      <LegalSection id="withdrawal-waiver" number="3b" title="EU right of withdrawal — waiver (herroepingsrecht)">
        <p className="mb-3">
          Consumers normally have 14 days to withdraw from a distance contract.
          For paid digital content supplied without a tangible medium, Article
          16(m) of Directive 2011/83/EU and Article 6:230p(g) of the Dutch Civil
          Code allow the withdrawal right to be lost only where all applicable
          statutory conditions are satisfied: the consumer gives prior express
          consent to supply beginning during the withdrawal period, acknowledges
          the resulting loss of the withdrawal right, and receives the legally
          required confirmation.
        </p>
        <p className="mb-3">
          Kickstart and AI+Social top-ups are made available immediately after
          successful payment. At checkout, the customer expressly requests and
          consents to immediate digital supply and records the required
          acknowledgement. This clause applies only to the extent permitted by
          mandatory law and does not exclude statutory conformity remedies or
          other non-waivable consumer rights.
        </p>
        <p>
          This clause concerns Kickstart lifetime tiers and AI+Social top-ups.
          Subscription cancellation is governed by clause 4 below.
        </p>
      </LegalSection>

      <LegalSection id="fictional-demo" number="3c" title="Seven-day fictional Business demo">
        <p>
          New registrations start with a seven-day demonstration using the
          fictional Elvayae Cleaning Services company. It demonstrates Business
          scenarios and does not grant a paid Business subscription or access
          to Enterprise ERP modules. Your own company data cannot be entered.
        </p>
        <p>
          The assistants work within the Elvayae scenarios. Uploads, downloads
          and exports are blocked. Email, invoice delivery and social publishing
          are simulated inside the demo; nothing is sent to real recipients or
          published to external accounts. Copy controls cannot prevent screenshots
          or access through browser developer tools.
        </p>
        <p>
          Demo access ends after seven days. Starting your own business workspace
          requires a separate plan purchase. The demo does not automatically
          convert to a paid subscription or trigger an automatic charge.
        </p>
      </LegalSection>

      <LegalSection id="cancellation" number="4" title="Cancellation & refunds (subscriptions only)">
        <p>
          For new Starter, Creator, Business and Agency paid subscriptions,
          the initial contract term is 12 months from paid activation,
          regardless of whether invoices are issued monthly or annually.
          Notice of non-renewal must be received at least two calendar months
          before the end of that initial term. The renewal term and applicable
          cancellation deadline must be expressly disclosed and accepted
          during checkout before a binding commitment arises. Customers may
          submit a cancellation request through Settings → Billing or via
          info@zynthoro.ai; the effective end date follows the accepted order
          and mandatory law. No change here retroactively extends an existing
          contract. Enterprise follows its specifically negotiated terms.
          Mandatory cancellation, withdrawal and other statutory rights remain
          unaffected.
        </p>
        <p className="mt-3">
          Kickstart lifetime purchases and AI+Social top-ups are one-time
          digital services and are governed by clauses 3a and 3b above.
          Refund and withdrawal rights are determined by the recorded checkout
          consent, the statutory classification of the supply and mandatory law.
        </p>
      </LegalSection>

      <LegalSection id="marketing-affiliates" number="4a" title="Marketing, referrals, leads & affiliate tracking">
        <p className="mb-3">
          Zynthoro may promote its services through advertising, marketing
          campaigns, referral links, discount codes, affiliate partners and
          other authorised marketing channels. An affiliate may receive
          compensation when an eligible referral results in a qualifying
          purchase. This compensation does not, by itself, increase the
          customer's agreed price or change the customer's contract rights.
        </p>
        <p className="mb-3">
          To identify referrals, attribute leads and purchases, measure campaign
          performance, prevent duplicate or fraudulent claims and administer
          partner commissions, Zynthoro and its authorised service providers
          may process referral identifiers, campaign parameters, coupon codes,
          relevant website events, lead or account identifiers and purchase or
          subscription status. Where applicable, cookies or similar tracking
          technologies are used only in accordance with applicable consent and
          privacy requirements. Tracking that requires consent must not be
          activated before valid consent has been obtained.
        </p>
        <p className="mb-3">
          The applicable privacy notice and cookie policy explain the purposes,
          legal bases, recipients, retention periods and available privacy
          choices for personal-data processing. Marketing emails and other
          electronic direct marketing are sent only where permitted by
          applicable law, including any required opt-in or unsubscribe rights.
          Submitting a lead or using a referral link does not itself constitute
          consent to unrelated marketing.
        </p>
        <p className="mb-3">
          Affiliates act as independent marketing partners unless expressly
          agreed otherwise. They may not misrepresent Zynthoro's prices,
          subscription commitments, cancellation rights or product capabilities,
          make unauthorised guarantees, impersonate Zynthoro, use misleading
          advertising or collect or share personal data without a lawful basis.
          Affiliate commissions, attribution rules, permitted promotional
          methods, reversals and payout conditions are governed by separate
          affiliate programme terms, not by a customer's subscription agreement.
        </p>
        <p>
          A referral or tracking record is not conclusive proof of a payable
          commission or a binding customer order. Purchases remain subject to
          the price, duration, cancellation provisions and other terms expressly
          accepted by the customer at checkout. Mandatory privacy, consumer
          and other legal rights are unaffected.
        </p>
      </LegalSection>

      <LegalSection id="acceptable" number="5" title="Acceptable use">
        <p>You agree not to:</p>
        <LegalList
          items={[
            "Use Zynthoro to build a competing AI ERP product or to benchmark it for that purpose.",
            "Submit data you do not have the right to process (no scraped or stolen PII).",
            "Attempt to jailbreak, prompt-inject or otherwise misuse the AI Assistants.",
            "Reverse-engineer, decompile or scrape the platform.",
            "Resell, sublicense or white-label the service without a written Enterprise agreement.",
            "Use the service for activities prohibited by EU/NL law (sanctions, money laundering, CSAM, etc.).",
          ]}
        />
      </LegalSection>

      <LegalSection id="ip" number="6" title="Intellectual property">
        <LegalList
          items={[
            "Zynthoro retains all rights, title and interest in the platform, including the AI Assistants' personas, prompts, UI and Zynthoro brand assets.",
            "You retain ownership of all data and content you upload ('Customer Data').",
            "You grant Zynthoro a limited licence to process Customer Data solely to deliver the service.",
            "AI outputs generated for you are owned by you, subject to Anthropic's and Google's underlying model terms.",
          ]}
        />
      </LegalSection>

      <LegalSection id="confidentiality" number="7" title="Confidentiality">
        <p>
          Each party will treat the other's non-public information as
          confidential and use it only to fulfil these Terms, for a period of
          five years after termination.
        </p>
      </LegalSection>

      <LegalSection id="warranties" number="8" title="Warranties & disclaimer">
        <p>
          Zynthoro is provided "as is" and "as available". We make no warranty
          that the AI Assistants will produce factually correct or
          legally-compliant output. <strong>You must independently verify any
          AI-generated content before relying on it for critical decisions.</strong>
        </p>
      </LegalSection>

      <LegalSection id="liability" number="9" title="Limitation of liability">
        <p>
          To the maximum extent permitted by law, Zynthoro's aggregate liability
          for any claim arising out of these Terms is limited to the fees paid
          by you in the 12 months preceding the claim. We are not liable for
          indirect, incidental, consequential or punitive damages, lost profits
          or lost data.
        </p>
      </LegalSection>

      <LegalSection id="termination" number="10" title="Termination">
        <p>
          We may suspend or terminate your account for material breach of these
          Terms, illegal activity, or non-payment after the grace period. Upon
          termination, you retain a 90-day window to export Customer Data,
          after which it is permanently deleted.
        </p>
      </LegalSection>

      <LegalSection id="law" number="11" title="Governing law & jurisdiction">
        <p>
          These Terms are governed by the laws of the Netherlands. Any dispute
          will be submitted to the exclusive jurisdiction of the courts of
          Amsterdam, without prejudice to your mandatory consumer rights.
        </p>
      </LegalSection>

      <LegalSection id="contact" number="12" title="Contact">
        <p>
          For legal notices, send to Casa Haya International BV (KvK 99196581):{" "}
          <a className="text-[var(--zy-blue)] font-semibold" href="mailto:info@zynthoro.ai">
            info@zynthoro.ai
          </a>
        </p>
      </LegalSection>
    </LegalLayout>
  );
}
